import { typeWithParameters } from '@angular/compiler/src/render3/util';
import { Component, OnInit, ViewChild } from '@angular/core';
import { DateHelperService } from '@helpers/helpers';
import { Meeting } from '@models/meeting.model';
import { ModalModel } from '@models/modal.model';
import { Snackbar } from '@models/models';
import { PBOptions } from '@models/progress-bar.model';
import { TranslateService } from '@ngx-translate/core';
import { MeetingService } from '@services/metting.service';
import { SnackbarService } from '@services/snackbar.service';
import { ProgressBarComponent } from '../progress-bar/progress-bar.component';

@Component({
    selector: 'app-meetings-management',
    templateUrl: './meetings-management.component.html',
    styleUrls: ['./meetings-management.component.scss'],
})
export class MeetingsManagementComponent implements OnInit {
    public today: Date = new Date();
    public selectedDay: Date;
    public show: boolean = false;
    public meetingID: number;
    public todaySchedule: Meeting[] = [];
    public nextMeetings: Meeting[] = [];
    public meetingList: Meeting[] = [];
    public currentRequest: Meeting;
    public newMeetingStatus: boolean;
    public new: boolean = false; // true if there are new pending meetings to aprove/reject
    public areYouSureModal: ModalModel;
    public snackbar: Snackbar;
    public meetingResponses: any = {
        accepted: '',
        rejected: '',
        error: '',
    };

    public progressBarOptions: PBOptions = {
        showText: true,
        backgroundColor: '#1a2d4b',
        callback: false,
    };

    @ViewChild(ProgressBarComponent, { static: false })
    pbComponent: ProgressBarComponent;

    constructor(
        private meetingService: MeetingService,
        private dateHelper: DateHelperService,
        private translate: TranslateService,
        private snackbarService: SnackbarService
    ) {}

    ngOnInit(): void {
        this.snackbar = this.snackbarService.snackbarItem;
    }

    getDayMeetings(day: Date): void {
        this.pbComponent.start();
        this.meetingList = [];
        const strToday = this.dateHelper.dateToStrFormated(day);

        this.meetingService.getMeetingList(strToday).subscribe(
            (response: Meeting[]) => {
                this.pbComponent.middle();
                this.meetingList = response.map((meeting: Meeting) => {
                    return new Meeting(
                        meeting.id,
                        meeting.max_time,
                        meeting.hour_selected,
                        meeting.date_selected,
                        meeting.is_accepted,
                        meeting.meeting_link,
                        meeting.user_data,
                        meeting.deal_name,
                        meeting.deal_id,
                        this.dateHelper.stringToDate(
                            meeting.date_selected,
                            meeting.hour_selected
                        ),
                        this.dateHelper.addTime(
                            this.dateHelper.stringToDate(
                                meeting.date_selected,
                                meeting.hour_selected
                            ),
                            +meeting.max_time
                        )
                    );
                });
                this.pbComponent.end();
                this.getDaySchedule(strToday);
            },
            (error) => {
                console.log(error);
            }
        );
    }

    getDaySchedule(day: String): void {
        this.todaySchedule = this.meetingList.filter(
            (meeting: Meeting) =>
                meeting.date_selected === day && meeting.is_accepted === true
        );
        this.nextMeetings = this.meetingList.filter(
            (meeting: Meeting) => meeting.is_accepted === false
        );
    }

    managePendingMeetings(meeting: Meeting, action: boolean): void {
        const data = {
            meeting: meeting.id,
            accepted: action,
            deal: meeting.deal_id,
        };
        this.meetingService.postMeeting(data, true).subscribe(
            () => {
                const snackbarText = action
                    ? this.meetingResponses.accepted
                    : this.meetingResponses.rejected;
                this.snackbar.show(snackbarText, 'success', true, 5000);
                const meetingIndex = this.nextMeetings.indexOf(meeting);
                this.nextMeetings.splice(meetingIndex, 1);
            },
            (error) => {
                console.log(error);
                this.snackbar.show(
                    this.meetingResponses.error,
                    'error',
                    true,
                    5000
                );
            }
        );
    }

    openConfirmModal(action: boolean, meetingRequest: Meeting) {
        this.currentRequest = meetingRequest;
        this.newMeetingStatus = action;

        if (!action) {
            this.translate
                .get('MEETINGS_MANAGEMENT.modal.areYouSure')
                .subscribe((translated: string) => {
                    this.areYouSureModal = new ModalModel(
                        false,
                        translated,
                        this.translate.instant(
                            'MEETINGS_MANAGEMENT.modal.rejectText'
                        )
                    );
                    this.meetingResponses.rejected = this.translate.instant(
                        'MEETINGS_MANAGEMENT.meetingRejected'
                    );
                    this.meetingResponses.error = this.translate.instant(
                        'MEETINGS_MANAGEMENT.meetingError'
                    );
                    this.areYouSureModal.open();
                });
        } else {
            this.translate
                .get('MEETINGS_MANAGEMENT.modal.areYouSure')
                .subscribe((translated: string) => {
                    this.areYouSureModal = new ModalModel(
                        false,
                        translated,
                        this.translate.instant(
                            'MEETINGS_MANAGEMENT.modal.acceptText'
                        )
                    );
                    this.meetingResponses.accepted = this.translate.instant(
                        'MEETINGS_MANAGEMENT.meetingAccepted'
                    );
                    this.meetingResponses.error = this.translate.instant(
                        'MEETINGS_MANAGEMENT.meetingError'
                    );
                    this.areYouSureModal.open();
                });
        }
    }

    /**
     * This is a callback function that it will be called when a popup send action back
     * @param modalResponse
     */
    getModalResult(modalResponse: boolean) {
        this.areYouSureModal.close();
        if (modalResponse) {
            this.managePendingMeetings(
                this.currentRequest,
                this.newMeetingStatus
            );
        }
    }

    /**
     * Given a day, load meetings for this day.
     * @param day
     */
    loadDay(day: Date): void {
        this.selectedDay = day;
        this.getDayMeetings(this.selectedDay);
    }

    /**
     * From a date one day up or one day down.
     * @param direction string : 'up' or 'down'
     */
    changeDay(direction: 'up' | 'down'): void {
        let minutesInDay = 1440; //60*24
        minutesInDay = direction === 'down' ? minutesInDay * -1 : minutesInDay;
        if (!this.selectedDay) {
            this.selectedDay = this.today;
        }
        this.selectedDay = this.dateHelper.addTime(
            this.selectedDay,
            minutesInDay
        );
        this.loadDay(this.selectedDay);
    }

    /**
     * show/hide popup
     */
    toggle(): void {
        this.show = !this.show;
        if (this.show) {
            this.selectedDay = this.today;
            this.getDayMeetings(this.today);
        }
    }
}
